import React, { useEffect, useMemo, useState } from 'react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { selectedQuote } from './FinancingGridCard';
import CurrencyInput from 'react-currency-input-field';
import {
    batteryCalculatorC4FromColumn,
    batterySvaC4,
    columnIncludesBattery,
    columnIncludesSolar,
    svaProductCodeFromColumn,
} from './quoteCreationProducts';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { GenerateQuoteSectionHead } from './GenerateQuoteSectionHead';
import type { GqSectionStat } from './GenerateQuoteSectionHead.types';
import './oneButton-css/RoofQuoteDetails.css';
import { Slider } from 'antd';
import { OBDSelect } from './form-elements';
import { Color } from 'antd/es/color-picker';
export interface SolarQuoteFormState {
    financeMethod: string;
    DesiredPPWNet: string;
    Deposit: string;
    UponCommencementOfPhysicalInstall: string;
    UponCompletionOfPhysicalInstall: string;
    UponCompletionOfTheContract: string;
}

export interface DropdownItem {
    Value: string | number;
    Text: string;
    PricingValue?: string | number;
}

export interface FieldObject {
    label: string;
    name: string;
    type: string;
    value: any;
    enable: boolean;
    errorMessage: string;
    message?: string | null;
    field?: string;
    dataType?: string;
    haveAccess?: boolean;
    options?: DropdownItem[];
    range?: {
        nzMin: number;
        nzMax: number;
        nzValue?: number;
        nzStep?: number;
        nzMarks: Record<string, string>;
        nzTooltipVisible: string;
        style?: string;
    };
}

export interface ApiResponse {
    OutputValues: { CellValue: any }[];
    [key: string]: any;
}

/** Default solar quote form rows (cloned into component state; options/enable flags are filled in from Excel/API). */
export const solarFields: FieldObject[] = [
    {
        label: 'Term & APR',
        name: 'TermAPR',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Pricing Method',
        name: 'PricingMethod',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Pricing Value',
        name: 'Price',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Escalator',
        name: 'Escalator',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Rate',
        name: 'Rate',
        type: 'textbox',
        value: '',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'EPC',
        name: 'EPC',
        type: 'textbox',
        value: '',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'PPW',
        name: 'PPW',
        type: 'textbox',
        value: '',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Down Payment',
        name: 'DownPayment',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Desired PPW (Net)',
        name: 'DesiredPPWNet',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Deposit Amount',
        name: 'Deposit',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Rebate Payment Type',
        name: 'RebateMethod',
        type: 'dropdown',
        value: '',
        enable: false,
        errorMessage: '',
        options: [],
    },
    {
        label: 'Tax',
        name: 'Tax',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Adder Cost',
        name: 'AdderCost',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Adder Price',
        name: 'AdderPrice',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Upon Commencement of Physical Install',
        name: 'UponCommencementOfPhysicalInstall',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Upon Completion of Physical Install',
        name: 'UponCompletionOfPhysicalInstall',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    },
    {
        label: 'Upon Completion of the Contract',
        name: 'UponCompletionOfTheContract',
        type: 'amount',
        value: '0',
        enable: false,
        errorMessage: '',
    }
];

/** Default fraction digits for currency-style amounts (PPW, dollars, etc.). */
const DEFAULT_AMOUNT_DECIMALS_LIMIT = 2;

/**
 * Pricing method option labels (normalized lowercase) where Price represents an energy rate ($/kWh).
 * Three decimals align with typical utility / Solar Rate tariff quoting precision.
 */
const PRICING_METHOD_LABELS_EXTRA_PRICE_DECIMALS: readonly string[] = ['rate', 'solar rate'];

export const SunnovaLoanOptions = ['Loan', 'Loan Storage', 'Lease Storage']



interface SolarQuoteDetailsProps {
    confirmQuoteCellKey: selectedQuote | null;
    priceQuote: any;
    theme: string;
    lineMode?: 'solar' | 'battery';
    lightreachEligibleProductData?: any[];
    lightreachPriceCapsData?: any[];
    updateLoaderCount: (loader: boolean) => void;
    updateErrorMessage: (message: string) => void;
    updateSolarDetails: (data: SolarDetails) => void;

}

export interface SolarDetails {
    opportunity_id: string;
    financePartnerName: string;
    financePartnerId?: number;
    contract_Type: string;
    epc: number;
    rate: number;
    ppw: number;
    pricingmethodName: string;
    pricingmethod: string | number;
    pricingValue: number;
    rebateMethod: string;
    rebate: number;
    Escalator: number;
    downPayment: number;
    tax: number;
    TermAndAPR: string;
    design: string;
    PermitPayer: string;
    TotalSaleAmount: number;
    DesiredPPWNet: number;
    RebateAmountPerWatt: number;
    AdderPrice: number;
    AdderCost: number;
    valid: boolean;
    grossSalesPricePerWatt: number;
    dealerFeePercentage: number;
    dealerEpc: number;
    Monthly_Finance_Payment_NoITCPayback: number;
    Monthly_Finance_Partner_Payment: number;
    ITC_Amount: number;
    Finance_Partner_Product_Id: string;
    Offer_Id: string;
    IsACH: string;
    Product_Type: string;
    productionRate: number;
    SolarProductionRate: number;
    Deposit_Amount: number;
    Financed_Amount: number;
    Total_Amount: number;
    UponCommencementOfPhysicalInstall: any;
    UponCompletionOfPhysicalInstall: any;
    UponCompletionOfTheContract: any;
    SyncProductInfo?: any
}

/**
 * Solar (or battery line) quote form: binds dynamic fields to Excel-backed pricing (cascadingDropDown)
 * and pushes a consolidated payload to the parent via `updateSolarDetails`.
 * Partner-specific branches (GoodLeap, Sunlight, IGS, etc.) override how totals and PPW are resolved.
 */
export const SolarQuoteDetails: React.FC<SolarQuoteDetailsProps> = ({
    confirmQuoteCellKey,
    priceQuote,
    theme,
    lineMode = 'solar',
    lightreachEligibleProductData = [],
    lightreachPriceCapsData: _lightreachPriceCapsData = [],
    updateLoaderCount,
    updateErrorMessage,
    updateSolarDetails
}) => {
    
    const [solarQuoteFormFields, setsolarQuoteFormFields] = useState<FieldObject[]>(JSON.parse(JSON.stringify(solarFields)));
    const [financeMethod, setFinanceMethod] = useState<string>('');
    const [eligibleProduct, setEligibleProduct] = useState<any[]>([]);

    const [pricingMethodArr, setPricingMethodArr] = useState<DropdownItem[]>([]);
    const [rebateMethods, setRebateMethods] = useState<DropdownItem[]>([]);
    const [escalatorArr, setEscalatorArr] = useState<DropdownItem[]>([]);
    const [termAPRArr, setTermAPRArr] = useState<DropdownItem[]>([]);
    const [solarFinanceMethods, setSolarFinanceMethods] = useState<DropdownItem[]>([]);
    const [downPaymentList, setDownPaymentList] = useState<DropdownItem[]>([]);
    const [financialArr, setFinancialArr] = useState<string[]>([]);
    const [Finance_Partner_Product_Id, setFinance_Partner_Product_Id] = useState('');
    const [Offer_Id, setOffer_Id] = useState('');
    const [existingOfferIdDetails, setExistingOfferIdDetails] = useState<any>(null);

    const [totalSaleAmount, setTotalSaleAmount] = useState(0);
    const [PPW, setPPW] = useState<any>(null);

    const [IsACH, setIsACH] = useState('');
    const [ProductType, setProductType] = useState('');

    const [productionRate, setProductionRate] = useState(0);

    const [financedAmount, setFinancedAmount] = useState(0);
    const [rebateAmount, setRebateAmount] = useState(0);
    const [grossSalesPricePerWatt, setGrossSalesPricePerWatt] = useState(0);
    const [dealerFeePercentage, setDealerFeePercentage] = useState(0);
    const [dealerEpc, setDealerEpc] = useState<any>();
    const [Monthly_Finance_Payment_NoITCPayback, setMonthly_Finance_Payment_NoITCPayback] = useState(0);
    const [ITCAmount, setITCAmount] = useState(0);
    const [Monthly_Finance_Partner_Payment, setMonthly_Finance_Partner_Payment] = useState(0);

    const [autoQuoteCreationInitiated, setAutoQuoteCreationInitiated] = useState(false);
    const [goodLeapValidationErrorMessage, setGoodLeapValidationErrorMessage] = useState('');

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);

    /** Latest outputs from the Solar Calculator Excel cascade (totals, PPW, payments). Used when form fields lag or partner logic needs computed values. */
    const salesAmoontDetailsRef = React.useRef<any>({});
    /** Last string from CurrencyInput `onValueChange` per field — blur runs totals with this so we never read stale state before re-render. */
    const amountDraftRef = React.useRef<Record<string, string>>({});
    const haveValidationErrors = (currentFields: FieldObject[]) => {
        return currentFields.find((item: any) => (item.enable || item.haveAccess) && item.message);
    }

    /** Reads a field from `solarQuoteFormFields`. Pass `currentFields` when you just mutated a copy and React state has not re-rendered yet. */
    const getFieldDetails = (field: string, currentFields?: FieldObject[]) => {
        return (currentFields || solarQuoteFormFields).find((item: any) => item.name === field);
    }

    const partnerNorm = (confirmQuoteCellKey?.partner || '').replace(/\s+/g, '').toLowerCase();
    const isLightreachPartner = partnerNorm === 'lightreach';
    const isGoodLeap = useMemo(
        () => (confirmQuoteCellKey?.partner || '').replace(/\s+/g, '').toLowerCase() === 'goodleap',
        [confirmQuoteCellKey?.partner],
    );

    /**
 * Parity with Angular `solar-detail-form.component.ts` (`validateLightreachQuote` and `updateSolarDetails`
 * for LightReach: `eligibleLightreachProductId` / `lightreachTotalSaleAmount` → `Offer_Id` / `TotalSaleAmount`).
 */
    function deriveLightreachOfferAndTotal(args: {
        isLightreach: boolean;
        products: any[];
        quoteLseIdRaw: any;
        financeMethod: string;
        gridMethord: string;
        priceRaw: any;
        escalatorRaw: any;
        currentFields?: FieldObject[];
    }): {
        eligibleLightreachProductId: string;
        lightreachTotalSaleAmount: number;
        inEligibleLightreachQuote: boolean;
        priceValidationMessage: string;
    } {
        const LIGHTREACH_PRICE_MSG =
            'Pricing chosen is not available from Lightreach, please click on the "Eligible Products" button above to review all the valid pricing available from Lightreach.';
        if (!args.isLightreach) {
            return {
                eligibleLightreachProductId: '',
                lightreachTotalSaleAmount: 0,
                inEligibleLightreachQuote: false,
                priceValidationMessage: '',
            };
        }
        const products = Array.isArray(args.products) ? args.products : [];
        if (products.length === 0) {
            return {
                eligibleLightreachProductId: '',
                lightreachTotalSaleAmount: 0,
                inEligibleLightreachQuote: true,
                priceValidationMessage: LIGHTREACH_PRICE_MSG,
            };
        }
        const quoteLseId = Number(args.quoteLseIdRaw);
        const lseMatches = products.filter((ele: any) => ele?.LseId == quoteLseId);
        if (lseMatches.length === 0) {
            return {
                eligibleLightreachProductId: '',
                lightreachTotalSaleAmount: 0,
                inEligibleLightreachQuote: true,
                priceValidationMessage: LIGHTREACH_PRICE_MSG,
            };
        }
        const contractType = String(args.financeMethod || args.gridMethord || '').trim().toLowerCase();
        const pricingValue = Number(args.priceRaw);
        const escalatorRaw = Number(args.escalatorRaw);

        const matchedProduct = lseMatches.find(
            (product: any) =>
                product?.Type == contractType
                && product?.KwhRate == pricingValue
                && product?.EscalationRate == Number(escalatorRaw/ 100).toFixed(4),
        );

        if (matchedProduct) {
            return {
                eligibleLightreachProductId: String(matchedProduct?.ProductId ?? '').trim(),
                lightreachTotalSaleAmount: Number(matchedProduct?.TotalEpcPayment) || 0,
                inEligibleLightreachQuote: false,
                priceValidationMessage: '',
            };
        }

        return {
            eligibleLightreachProductId: '',
            lightreachTotalSaleAmount: 0,
            inEligibleLightreachQuote: true,
            priceValidationMessage: LIGHTREACH_PRICE_MSG,
        };
    }

    const getLightreachDerived = (currentFields?: FieldObject[]) => {

        const f = (name: string) => getFieldDetails(name);
        const { priceRaw, escalatorRaw } = {
            priceRaw: f('Price')?.value,
            escalatorRaw: f('Escalator')?.value,
        };
        return deriveLightreachOfferAndTotal({
            isLightreach: isLightreachPartner,
            products: lightreachEligibleProductData,
            quoteLseIdRaw: priceQuote?.LSEDID,
            financeMethod: confirmQuoteCellKey?.methord || '',
            gridMethord: confirmQuoteCellKey?.methord || '',
            priceRaw,
            escalatorRaw,
            currentFields
        });
    };

    const lightreachDerived = useMemo(
        () => getLightreachDerived(solarQuoteFormFields),
        [
            isLightreachPartner,
            lightreachEligibleProductData,
            priceQuote?.LSEDID,
            financeMethod,
            confirmQuoteCellKey?.methord,
            solarQuoteFormFields,
        ],
    );

    useEffect(() => {
        if (!isLightreachPartner) return;
        const nextMessage = lightreachDerived.priceValidationMessage ?? '';
        setsolarQuoteFormFields((prev) => {
            const priceField = prev.find((e) => e.name === 'Price');
            if (!priceField || (priceField.message ?? '') === nextMessage) return prev;
            return prev.map((el) => (el.name === 'Price' ? { ...el, message: nextMessage } : el));
        });
    }, [isLightreachPartner, lightreachDerived.priceValidationMessage]);

    useEffect(() => {
        if (
            isLightreachPartner &&
            lightreachDerived.eligibleLightreachProductId &&
            lightreachDerived.lightreachTotalSaleAmount > 0
        ) {
            updateErrorMessage('');
        }
    }, [
        isLightreachPartner,
        lightreachDerived.eligibleLightreachProductId,
        lightreachDerived.lightreachTotalSaleAmount,
        updateErrorMessage,
    ]);

    const normalizePartnerToken = (value: any): string =>
        String(value ?? '')
            .replace(/\s+/g, '')
            .trim()
            .toLowerCase();

    const getDefaultPricingValue = (returnMinPricingValue: boolean = false, currentFields?: FieldObject[]) => {
        if (financeMethod === 'Cash Contract') {
            return 0;
        }
        let selectedPricingValue = pricingMethodArr.find(
            (a) => a.Value == getFieldDetails('PricingMethod', currentFields)?.value,
        );
        const systemSizeValue = priceQuote?.EPCCalc?.SystemSizeValue || 0;

        if (selectedPricingValue?.PricingValue == 'Dealer EPC Per Watt' || selectedPricingValue?.Text == 'Desired PPW (Net)') {
            return 0;
        } else if (selectedPricingValue?.PricingValue == 'EPC to Dealer' || selectedPricingValue?.Text == 'EPC') {
            return returnMinPricingValue === true ? (systemSizeValue * 1000 * 2.5) : 0;
        }
        return 0;
    }

    /**
     * Single entry point for UI field updates (dropdowns always; amount fields on blur unless `priventDefault` is true).
     * Mutates a temp array, commits with `setsolarQuoteFormFields`, then runs partner-specific follow-up:
     * GoodLeap → API/total pipeline; Sunlight → dedicated handler; others → Excel total (`updateTotalSaleAmount`) + parent sync (`updateSolarObject`).
     * Use `getFieldDetails(..., solarQuoteFormFieldTemp)` below so validation sees the value just written (state is async).
     *
     * `resetPriceOnPricingMethodChange`: pass true only from the Pricing Method or Escalator dropdown `onChange`.
     * API/init code calls `setSolarQuoteFromValues('PricingMethod' | 'Escalator', …)` without it so Price is not cleared on load.
     */
    const setSolarQuoteFromValues = (
        field: string,
        value: any,
        priventDefault: boolean = false,
        resetPriceOnPricingMethodChange: boolean = false,
    ) => {
        let solarQuoteFormFieldTemp = [...solarQuoteFormFields];
        solarQuoteFormFieldTemp.forEach(ele => {
            if (ele.name == field) { ele.value = value; }
            // Only sync the Price slider from `value` when `field` is Price. Otherwise any other field
            // update (e.g. Escalator) would clobber `range.nzValue` with the wrong value.
            if (field === 'Price' && ele.name === 'Price' && ele.type === 'Slider' && ele.range) {
                ele.range.nzValue = Number(value);
            }
            if (field === 'DesiredPPWNet' && ele.name === 'Price' && ele.type !== 'Slider'){
                ele.value = value;
            }
            if (field == 'PricingMethod' && ele.name == 'Price') {
                const pmOptions = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.options || [];
                const pmVal = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.value;
                const PricingMethodValue: string =
                    pmOptions.find((o) => o.Value == pmVal)?.Text || 'Pricing Value';
                ele.label = PricingMethodValue;
                if (resetPriceOnPricingMethodChange) {
                    ele.value = '0';
                }
            }
            if (field == 'Escalator' && ele.name == 'Price' && resetPriceOnPricingMethodChange) {
                ele.value = '0';
                if (ele.type === 'Slider' && ele.range) {
                    ele.range.nzValue = 0;
                }
            }
            if (ele.name == 'Price' && (ele.enable || ele.haveAccess) ) {
                const selectedPricingValue = pricingMethodArr.find(
                    (p) => p.Value == getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.value,
                );
                const priceVal = getFieldDetails('Price', solarQuoteFormFieldTemp)?.value;
                if ((selectedPricingValue?.PricingValue == 'EPC to Dealer' || selectedPricingValue?.Text == 'EPC')
                    && (Number(priceVal) < getDefaultPricingValue(true, solarQuoteFormFieldTemp))) {
                    ele.message = 'The entered value should not be less than $' + getDefaultPricingValue(true, solarQuoteFormFieldTemp);
                } else if ((selectedPricingValue?.PricingValue == 'Dealer EPC Per Watt' || selectedPricingValue?.Text == 'Desired PPW (Net)')
                    && (Number(priceVal) < 2.5)) {
                    ele.message = 'The entered value should not be less than $2.5';
                } else if(ele.type === 'Slider'){
                    if(ele.range?.nzMin && Number(priceVal) < ele.range?.nzMin) {
                        ele.message = `The entered value should not be less than ${ele.range?.nzMin}`;
                    } else if(ele.range?.nzMax && Number(priceVal) > ele.range?.nzMax) {
                        ele.message = `The entered value should not be greater than ${ele.range?.nzMax}`;
                    } else {
                        ele.message = '';
                    }
                } else {
                    ele.message = '';
                }
            }

            if(ele.name == "DesiredPPWNet" && (ele.enable || ele.haveAccess)) {
                const desiredFromTemp = Number(getFieldDetails(ele.name, solarQuoteFormFieldTemp)?.value ?? 0);
                if (desiredFromTemp < 2.5) {
                    ele.message = 'The entered value should not be less than $2.5';
                } else {
                    ele.message = '';
                }
            }

            if(ele.name == "Deposit" && confirmQuoteCellKey?.partner == 'Cash' && (ele.enable || ele.haveAccess)) {
                const depositFromTemp = Number(getFieldDetails(ele.name, solarQuoteFormFieldTemp)?.value ?? 0);

                if (totalSaleAmount != 0 && depositFromTemp > totalSaleAmount) {
                    ele.message = `The entered Deposit Amount cannot be greater than $ ${Number(totalSaleAmount).toFixed(2)}`;
                } else {
                    ele.message = '';
                }
            }


        })
        if (field === 'PricingMethod' || field === 'Escalator') {
            applyPriceFieldFromPricingMethod(
                solarQuoteFormFieldTemp,
                financialArr,
                resetPriceOnPricingMethodChange && field === 'Escalator',
            );
        }
        setsolarQuoteFormFields(solarQuoteFormFieldTemp);
        // `priventDefault` is set for amount `onChange` so we only recalc on `onBlur` (avoids Excel/API spam per keystroke).
        if (!priventDefault) {
            if (!getFieldDetails(field, solarQuoteFormFieldTemp)?.message) {
                if (isGoodLeap) {

                    if (confirmQuoteCellKey?.payload?.offerId) {
                        let offerId = ''
                        if (existingOfferIdDetails && existingOfferIdDetails.FinanceMethod == financeMethod
                            && ((financeMethod.includes('Loan') && getFieldDetails('TermAPR')?.value?.trim() == existingOfferIdDetails.TermAPR.trim())
                                || (!financeMethod.includes('Loan') && existingOfferIdDetails.Escalator == getFieldDetails('Escalator')?.value
                                    && !existingOfferIdDetails.isUtilityUpdated))) {
                            offerId = existingOfferIdDetails.OfferId;
                            if (confirmQuoteCellKey) confirmQuoteCellKey.payload.offerId = existingOfferIdDetails.OfferId;
                            setOffer_Id(existingOfferIdDetails.OfferId)
                        } else {
                            setOffer_Id(confirmQuoteCellKey?.payload?.offerId);
                            offerId = confirmQuoteCellKey?.payload?.offerId;
                        }
                        updateGoodLeapTotalSaleAmount(false, offerId, solarQuoteFormFieldTemp);
                    } else {
                        goodLeapDropDownChange();
                    }

                } else if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunlight') getSunlightPartnerTotalSaleAmount();
                else if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'lightreach') {
                    let element = getFieldDetails('Price', solarQuoteFormFieldTemp);
                    if(element?.enable && element?.dataType === 'Slider') {
                        getRateMinMaxForLightreach(
                            element,
                            solarQuoteFormFieldTemp,
                            resetPriceOnPricingMethodChange && field === 'Escalator',
                        );
                    }
                    updateSolarObject(false, solarQuoteFormFieldTemp);
                } else if (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'igs') {
                    const leadId = opportunityDetails?.Finance_Partner_Sync_LeadID
                        ?? priceQuote?.Finance_Partner_Sync_LeadID;
                    if (!leadId){
                        updateErrorMessage("Lead ID not found, please save the lead first");
                        return;
                    } else {
                        updateTotalSaleAmount();
                        updateSolarObject(false, solarQuoteFormFieldTemp);
                    }
                } else {
                    // Default path: refresh totals from Solar Calculator sheet, then push form + ref data to parent.
                    updateTotalSaleAmount();
                    updateSolarObject(false, solarQuoteFormFieldTemp);
                }
            }
        }
    }



    const getTermAndAPR = (data: string) => {
        return data.replace(' w/ ACH', '').replace('w/o ACH', '').trim();
    }

    const getExcelFinancePartner = (partner: string) => {
        switch (partner) {
            case 'GoodLeap':
            case 'Goodleap':
                partner = 'Good Leap';
        }
        return partner;
    }

    const getSvaC4 = (methodOverride?: string) => {
        if (lineMode === 'battery') {
            return batterySvaC4(
                confirmQuoteCellKey?.partner || '',
                confirmQuoteCellKey?.product,
                methodOverride ?? financeMethod ?? (confirmQuoteCellKey?.methord as string) ?? '',
            );
        }
        return svaProductCodeFromColumn(confirmQuoteCellKey?.product);
    };

    /** Calls Excel "Solar Calculator" / "Solar IO" via cascadingDropDown; writes results to state and `salesAmoontDetailsRef`. Async — callers that need fresh totals should use the `.then` path or ref after completion. */
    const updateTotalSaleAmount = (flag: boolean = false) => {
        if (lineMode === 'battery') return;

        let selectedPricingValue = null
        if(getFieldDetails('PricingMethod')?.value != null && getFieldDetails('PricingMethod')?.value != '' && getFieldDetails('PricingMethod')?.value != undefined)
            selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod')?.value || a.Text == getFieldDetails('PricingMethod')?.value);
        let payload = {
            "ExcelFileName": "Solar Calculator",
            "SheetName": "Solar IO",
            "InputValues": [
                {
                    "Type": "String",
                    "CellNumber": "C3",
                    "CellValue": priceQuote.InstallationState
                },
                {
                    "Type": "String",
                    "CellNumber": "C4",
                    "CellValue": priceQuote.EPCCalc.SystemSizeValue
                },
                {
                    "Type": "String",
                    "CellNumber": "C5",
                    "CellValue": getSvaC4()
                },
                {
                    "Type": "String",
                    "CellNumber": "C6",
                    "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C7",
                    "CellValue": confirmQuoteCellKey?.methord || ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C8",
                    "CellValue": (getFieldDetails('TermAPR')?.value) ? getTermAndAPR(getFieldDetails('TermAPR')?.value.trim()) : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C9",
                    "CellValue": (selectedPricingValue?.Text == 'Desired PPW (Net)' || selectedPricingValue?.Text == 'EPC' 
                        || confirmQuoteCellKey?.partner == 'Cash') ?
                        ((!getFieldDetails('Price')?.value || getFieldDetails('Price')?.value == 0) ? getFieldDetails('DesiredPPWNet')?.value : getFieldDetails('Price')?.value) : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C10",
                    "CellValue": (getFieldDetails('Deposit')?.value) ? getFieldDetails('Deposit')?.value : getFieldDetails('DownPayment')?.value
                },
                {
                    "Type": "String",
                    "CellNumber": "C11",
                    "CellValue": getFieldDetails('RebateMethod')?.value === "Per Watt" ? getFieldDetails('RebateValue')?.value : ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C12",
                    "CellValue": getFieldDetails('Tax')?.value || 0
                },
                {
                    "Type": "String",
                    "CellNumber": "C13",
                    "CellValue": (confirmQuoteCellKey?.partner == 'Cash' ? getFieldDetails('AdderPrice')?.value : getFieldDetails('AdderCost')?.value || 0)
                },
                {
                    "Type": "String",
                    "CellNumber": "C14",
                    "CellValue": selectedPricingValue?.Text
                },
                {
                    "Type": "String",
                    "CellNumber": "C15",
                    "CellValue": priceQuote.TSRF
                }
            ],
            "OutputCells": ["F6", "F7", "F5", "F3", "F9", "F4", "F10", "F8", "F12"]
        }
        if (financeMethod === 'Loan' && selectedPricingValue?.Text === 'EPC' && confirmQuoteCellKey?.partner == 'Sunlight') {
            setTotalSaleAmount(getFieldDetails('Price')?.value);
            setFinancedAmount((getFieldDetails('Deposit')?.value) ? totalSaleAmount - Number(getFieldDetails('Deposit')?.value) : totalSaleAmount)
        } else {
            setTotalSaleAmount(0);
            setFinancedAmount(0);
        }
        updateLoaderCount(true);
        salesAmoontDetailsRef.current = {};
        masterServices.cascadingDropDown(payload).then((data: ApiResponse) => {
            setRebateAmount(0);

            salesAmoontDetailsRef.current = {
                TotalSaleAmount: data[0].CellValue,
                FinancedAmount: data[1].CellValue,
                GrossSalesPricePerWatt: data[2].CellValue,
                DealerFeePercentage: data[3].CellValue,
                DealerEpc: data[1].CellValue,
                DealerTotalSaleAmount: data[4].CellValue,
                DealerFinancedAmount: data[5].CellValue,
                DealerGrossSalesPricePerWatt: data[6].CellValue,
                DealerDealerFeePercentage: data[7].CellValue,
                DealerDealerEpc: data[8].CellValue
            }
            setTotalSaleAmount(data[0].CellValue);
            if (selectedPricingValue?.Text == 'Desired PPW (Net)') {
                switch (getFieldDetails('RebateMethod')?.value) {
                    case 'Per Watt':
                        setRebateAmount(data.OutputValues[5].CellValue);
                        break;
                    case 'Fixed':
                        setRebateAmount(getFieldDetails('RebateValue')?.value);
                        break;
                    default:
                }
            } else {
                const systemSizeValue = priceQuote?.EPCCalc?.SystemSizeValue || 0;
                switch (getFieldDetails('RebateMethod')?.value) {
                    case 'Per Watt':
                        setRebateAmount(systemSizeValue * 1000 * getFieldDetails('RebateValue')?.value);
                        break;
                    case 'Fixed':
                        setRebateAmount(getFieldDetails('RebateValue')?.value);
                        break;
                    default:
                }
            }  
            if(selectedPricingValue?.Text == 'Desired PPW (Net)' || selectedPricingValue?.Text == 'EPC' 
                || confirmQuoteCellKey?.partner == 'Cash' && !confirmQuoteCellKey?.clonedQuoteDetails){
                if(getFieldDetails('DesiredPPWNet')?.value != getFieldDetails('Price')?.value && getFieldDetails('Price')?.enable) {
                    setSolarQuoteFromValues('DesiredPPWNet', getFieldDetails('Price')?.value);
                } else if(!getFieldDetails('DesiredPPWNet')?.value || getFieldDetails('DesiredPPWNet')?.value == 0){
                    if(confirmQuoteCellKey?.ExcelOffer?.startingPPW ){
                        setSolarQuoteFromValues('Price', confirmQuoteCellKey?.ExcelOffer?.startingPPW);
                        setSolarQuoteFromValues('DesiredPPWNet', confirmQuoteCellKey?.ExcelOffer?.startingPPW);
                    }
                }
            }
            setGrossSalesPricePerWatt(data[2].CellValue);
            setDealerFeePercentage(data[3].CellValue);
            setDealerEpc(data[1].CellValue);
            setMonthly_Finance_Payment_NoITCPayback(data[4].CellValue); //F9
            setITCAmount(data[6].CellValue);
            setMonthly_Finance_Partner_Payment(data[7].CellValue); //F8
            if (!(financeMethod === 'Loan' && selectedPricingValue?.Text === 'EPC' && confirmQuoteCellKey?.partner == 'Sunlight')) {
                setFinancedAmount(Number(data[8].CellValue)); //F12
            }
            updateSolarObject(true, solarQuoteFormFields);
            updateLoaderCount(false);
        }).catch(() => {
            updateLoaderCount(false);
        });
    }

    /** Parse display string from CurrencyInput / masked inputs (strip $, commas, etc.). */
    const parseAmountDisplayString = (display: string) => {
        const n = parseFloat(String(display).replace(/[^0-9.-]/g, ''));
        return Number.isFinite(n) ? n : 0;
    };

    /** Keeps Price slider + manual CurrencyInput within API-provided min/max (single value, no duplicate React state). */
    const clampPriceToSliderRange = (n: number, min?: number, max?: number) => {
        if (!Number.isFinite(n)) n = 0;
        const hasLo = min !== undefined && Number.isFinite(min);
        const hasHi = max !== undefined && Number.isFinite(max);
        if (!hasLo && !hasHi) return n;
        const lo = hasLo ? (min as number) : n;
        const hi = hasHi ? (max as number) : n;
        if (hi < lo) return n;
        return Math.min(Math.max(n, lo), hi);
    };

    const validateFieldValue = (event: React.KeyboardEvent<HTMLInputElement>, field: FieldObject) => {
        if (field.type !== 'amount' && !(field.name === 'Price' && field.type === 'Slider')) return;
        const isControlKey = event.key.length > 1;
        if (isControlKey) return;

        // CurrencyInput formats with "$" and grouping commas — a /\d*\.?\d*$/ regex on raw `value` always fails and blocked all keys.
        // Decimals and negatives are enforced by CurrencyInput props; optional live messages only below.
        const { value } = event.currentTarget;
        const numericGuess = parseAmountDisplayString(value);

        if (field.name === 'DesiredPPWNet') {
            const message =
                numericGuess > 0 && numericGuess < 2.5
                    ? 'The entered value should not be less than $2.5'
                    : null;
            setsolarQuoteFormFields((prev) =>
                prev.map((ele) => (ele.name === field.name ? { ...ele, message } : ele)),
            );
        }
        if (field.name === 'Deposit') {
            const message =
                totalSaleAmount && numericGuess > totalSaleAmount
                    ? `The entered Deposit Amount cannot be greater than ${totalSaleAmount}`
                    : null;
            setsolarQuoteFormFields((prev) =>
                prev.map((ele) => (ele.name === field.name ? { ...ele, message } : ele)),
            );
        }
    };

    const getDownpaymentList = (financeType: any) => {
        if (!confirmQuoteCellKey?.clonedQuoteDetails) setSolarQuoteFromValues('DownPayment', 0)
        if (financeType != '') {
            if (confirmQuoteCellKey?.partner !== 'Sunnova') {
                if (confirmQuoteCellKey?.partner != 'IGS') {
                    solarQuoteFormFields.forEach((ele: any) => {
                        let fmOptions = solarFinanceMethods.filter((elem: any) => elem.Text == 'Prepaid PPA-EZ');
                        if (fmOptions.length > 0) {
                            if (ele.field == 'DownPayment' && fmOptions[0].Value == financeMethod) {
                                ele.enable = true;
                                ele.dataType = 'Amount';
                            }
                        } else {
                            if (ele.field == 'DownPayment') {
                                ele.enable = true;
                                ele.dataType = 'Amount';
                            }
                        }
                    })
                }
            }
        }
    }

    const enableFields = (field: string) => {
        let solarQuoteFormFieldsTemp = [...solarQuoteFormFields];
        solarQuoteFormFieldsTemp.forEach(element => {
            if (element.label == field.trim() || element.name == field.trim()) {
                element.enable = true;
            }
        });
        setsolarQuoteFormFields(solarQuoteFormFieldsTemp);
    }

    const setEscolatorRange = (escalatorArr: any[]) => {
        let field = solarQuoteFormFields.find(element => element.name == 'Escalator');
        if (field?.enable) {
            let marks: any[] = []
            let escalatorArrTemp = [...escalatorArr];
            let nzMin = Number(escalatorArrTemp[0].Text);
            let nzMax = Number(escalatorArrTemp[0].Text);

            escalatorArrTemp.forEach(ele => {
                if (nzMin > Number(ele.Text)) nzMin = Number(ele.Text);
                else if (nzMax < Number(ele.Text)) nzMax = Number(ele.Text);
                marks[Number(ele.Text)] = '$' + String(ele.Text);
            });
            let rage: any = {
                nzMin: nzMin,
                nzMax: nzMax,
                nzMarks: marks,
                nzTooltipVisible: 'default'
            };
            setsolarQuoteFormFields((prev) => prev.map((ele: any) => (ele.name == field.name ? { ...ele, rage } : ele)));
        }
    }

    const retrieveSliderDetails = (
        field: any,
        currentFields?: FieldObject[],
        pricingMethodArr?: any[],
        skipAutoPriceUpdate: boolean = false,
    ) => {
        if (lineMode === 'battery') return;
        let selectedPricingValue = pricingMethodArr?.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        const payload = {
            ExcelFileName: "Sunnova Financial Data",
            SheetName: "SVA Financing Options",
            InputValues: [
                { CellNumber: "B4", CellValue: priceQuote.InstallationState, Type: "String" },
                { CellNumber: "C4", CellValue: getSvaC4(), Type: "String" },
                { CellNumber: "D4", CellValue: (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '', Type: "String" },
                { CellNumber: "E8", CellValue: priceQuote.ExcelUtilityCompanyName, Type: "String" },
                { CellNumber: "F4", CellValue: confirmQuoteCellKey?.methord, Type: "String" },
                { CellNumber: "K4", CellValue: selectedPricingValue?.Text, Type: "String" },
                { CellNumber: "M4", CellValue: getFieldDetails('Escalator', currentFields)?.value, Type: "String" }
            ],
            OutputCells: ["K11"]
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(payload).then((data: ApiResponse) => {
            if (data[0].CellValue && confirmQuoteCellKey?.partner?.toLocaleLowerCase() != 'goodleap' 
                && confirmQuoteCellKey?.partner?.toLocaleLowerCase() != 'lightreach'){
                const value = JSON.parse(data[0].CellValue);
                let marks: any = {};
                let red = 0, yellow = 0, green = 100;
                if (confirmQuoteCellKey?.partner == "Sunnova") {
                    if (selectedPricingValue?.Text === 'PPW') {
                        marks[value['SunnovaPPWMin']] = '$' + String(value['SunnovaPPWMin']);
                        marks[value['SunnovaPPWMax']] = '$' + String(value['SunnovaPPWMax']);
                        const total = value['SunnovaPPWMax'] - value['SunnovaPPWMin'];
                        red = Math.round(((value.TrinityPPWFloor - value.SunnovaPPWMin) / total) * 100);
                        yellow = Math.round(((value.TrinityPPWMinimum - value.TrinityPPWFloor) / total) * 100);
                        green = Math.round(((value.SunnovaPPWMax - value.TrinityPPWMinimum) / total) * 100);
                    } else {
                        marks[value['SunnovaSolar RateMin']] = '$' + String(value['SunnovaSolar RateMin']);
                        marks[value['SunnovaSolar RateMax']] = '$' + String(value['SunnovaSolar RateMax']);
                    }
                    const style = `--red-area:${red}%;--yellow-area:${yellow}%;--red-yellow-area:${red + yellow}%;--green-area:${green}%;--slider-color:lightgreen`
                    field.range = {
                        nzMin: selectedPricingValue?.Text === 'PPW' ? value['SunnovaPPWMin'] : value['SunnovaSolar RateMin'],
                        nzMax: selectedPricingValue?.Text === 'PPW' ? value['SunnovaPPWMax'] : value['SunnovaSolar RateMax'],
                        nzValue: selectedPricingValue?.Text === 'PPW' ? value['SunnovaPPWStart'] : value['SunnovaSolar RateStart'],
                        nzStep: selectedPricingValue?.Text === 'PPW' ? 0.01 : .001,
                        nzMarks: marks,
                        nzTooltipVisible: 'default',
                        style: style
                    };
                } else {

                    const outputValue = value.Output;
                    const escalatorValue = getFieldDetails('Escalator', currentFields)?.value;
                    const minValue = +outputValue[`${escalatorValue} Rate Min`];
                    const maxValue = +outputValue[`${escalatorValue} Rate Max`];
                    const startValue = +outputValue[`${escalatorValue} Rate Start`];
                    const splitPoint = ((startValue - minValue) / (maxValue - minValue)) * 100;
                    yellow = splitPoint;
                    green = 100 - yellow;
                    red = 0;
                    if (selectedPricingValue?.Text === 'PPW') {
                        marks[value.IGSRateMin] = '$' + String(value.IGSRateMin);
                        marks[value.IGSRateMax] = '$' + String(value.IGSRateMax);
                    } else {
                        marks[minValue] = '$' + String(minValue);
                        marks[maxValue] = '$' + String(maxValue);
                        marks[startValue] = {
                            style: {
                                transform: 'translateX(-100%)',
                                whiteSpace: 'nowrap'
                            },
                            label: '$' + startValue
                        };
                    }
                    const style = `--red-area:${red}%;--yellow-area:${yellow}%;--green-area:${green}%;--slider-color:linear-gradient(to right, yellow 0%, yellow ${yellow}%, lightgreen ${yellow}%, lightgreen 100%);--slider-track-color:transparent;--slider-segment-display:black`
                    field.range = {
                        nzMin: selectedPricingValue?.Text === 'PPW' ? value.IGSRateMin : minValue,
                        nzMax: selectedPricingValue?.Text === 'PPW' ? value.IGSRateMax : maxValue,
                        nzValue: selectedPricingValue?.Text === 'PPW' ? value.IGSRateStart : startValue,
                        nzStep: selectedPricingValue?.Text === 'PPW' ? 0.01 : .001,
                        nzMarks: marks,
                        nzTooltipVisible: 'default',
                        style: style
                    };
                }
                let sliderValue = (autoQuoteCreationInitiated && ((confirmQuoteCellKey?.partner == 'Sunnova' && financeMethod == 'PPA-EZ')
                    || (confirmQuoteCellKey?.partner == 'IGS' && financeMethod == 'Lease')))
                    ? Number(priceQuote?.RequestedPPARate) : field.range.nzValue;
                if(selectedPricingValue?.Text == 'Solar Rate'){
                    sliderValue = (confirmQuoteCellKey?.ExcelOffer)? confirmQuoteCellKey?.ExcelOffer.startingRate ?? field.range.nzValue : field.range.nzValue;
                }
                if (!confirmQuoteCellKey?.clonedQuoteDetails && !skipAutoPriceUpdate) {
                    setSolarQuoteFromValues('Price', sliderValue);
                }
                
                field.enable = true;
                let solarQuoteFormFieldTemp = [...solarQuoteFormFields];
                solarQuoteFormFieldTemp.forEach(ele => {
                    if (ele.name == field.name) {
                        ele.range = field.range;
                        ele.value = skipAutoPriceUpdate ? '0' : field.range.nzValue;
                    }
                });
                setsolarQuoteFormFields(solarQuoteFormFieldTemp);
                updateSolarObject(false, currentFields);
            }
            updateLoaderCount(false);
        }).catch(() => {
            updateLoaderCount(false);
        });
    }

    const getSliderCssVars = (style?: string): React.CSSProperties => {
        if (!style) return {};
        return style.split(';').reduce((acc, part) => {
            const [key, ...valueParts] = part.split(':');
            const value = valueParts.join(':').trim();
            if (key?.trim() && value) {
                (acc as Record<string, string>)[key.trim()] = value;
            }
            return acc;
        }, {} as React.CSSProperties);
    };

    const getRateMinMaxForLightreach = (
        field: any,
        currentFields?: FieldObject[],
        skipAutoPriceUpdate: boolean = false,
    ) => {  
        const leadId = opportunityDetails?.Finance_Partner_Sync_LeadID
            ?? priceQuote?.Finance_Partner_Sync_LeadID;
        if (!leadId){
            updateErrorMessage("Lead ID not found, please save the lead first");
            return;
        } 
        const Escalator = getFieldDetails('Escalator', currentFields)?.value;
        if(Escalator !== undefined && Escalator !== null && Escalator !== ''){
            const payload = {
                solar_Rate: 0,
                escalationRate: String(Escalator),
                financeMethod: confirmQuoteCellKey?.methord,
                stateCode: priceQuote.InstallationState,
                utilityCompany: priceQuote.UtilityCompanyName,
                leadId: leadId,
                opportunityId: String(priceQuote.Opportunity_Id),
            }
            updateLoaderCount(true);
            quoteServices.getRateMinMaxForLightreach(payload).then((data: ApiResponse) => {
                if (data) {
                    let marks: any = {};
                    let red = 0, yellow = 0, green = 100;
                    const style = `--red-area:${red}%;--yellow-area:${yellow}%;--red-yellow-area:${red + yellow}%;--green-area:${green}%;--slider-color:lightgreen`
                    field.range = {
                        nzMin: data.MinPrice,
                        nzMax: data.MaxPrice,
                        nzValue: data.MinPrice,
                        nzStep: .005,
                        nzMarks: marks,
                        nzTooltipVisible: 'default',
                        style: style
                    };
                    const isLightreachPartner = normalizePartnerToken(confirmQuoteCellKey?.partner) === 'lightreach';
                    let sliderValue:any = field.range.nzValue;
                    if(isLightreachPartner && !!confirmQuoteCellKey?.ExcelOffer?.startingRate){
                        sliderValue = confirmQuoteCellKey?.ExcelOffer.startingRate ;
                    } else {
                        if(!!priceQuote.RequestedPPARate && !isLightreachPartner){
                            sliderValue = Number(priceQuote.RequestedPPARate);
                        } 
                    }
                    field.enable = true;
                    if (!confirmQuoteCellKey?.clonedQuoteDetails && !skipAutoPriceUpdate) {
                        setSolarQuoteFromValues('Price', sliderValue, true);
                    }
                    updateSolarObject(false, currentFields);
                }
                updateLoaderCount(false);
            }).catch(() => {
                updateLoaderCount(false);
            });
        }
    }

    /**
     * Price as slider only for rate-based methods (PPW, Solar Rate, Rate). All other methods use a single CurrencyInput.
     * Called from `updateOtherFields` (Excel init) and from `setSolarQuoteFromValues` when the user changes Pricing Method
     * so `field.type` is not left as `Slider` when switching to non-rate methods (e.g. Monthly Payment on IGS).
     */
    const applyPriceFieldFromPricingMethod = (
        solarQuoteFormFieldTemp: FieldObject[],
        financialFieldsArr?: string[],
        skipAutoPriceUpdate: boolean = false,
    ) => {
        if (lineMode === 'battery') return;
        const pricingMethodOptions = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.options || [];
        const pmVal = getFieldDetails('PricingMethod', solarQuoteFormFieldTemp)?.value;
        const resolvedPricingMethod = pricingMethodOptions.find(
            (a: any) => String(a.Value) == String(pmVal),
        );
        const isRateBased = ['ppw', 'solar rate', 'rate'].includes(
            (resolvedPricingMethod?.Text ?? '').trim().toLowerCase(),
        );
        const element = solarQuoteFormFieldTemp.find((e) => e.name === 'Price');
        if (!element) return;

        if (!isGoodLeap && isRateBased) {
            element.type = 'Slider';
            if (confirmQuoteCellKey?.partner?.toLowerCase() === 'lightreach') {
                if(financialFieldsArr && financialFieldsArr.length > 0) {
                    const data: any = financialFieldsArr?.find(
                        (el) =>
                            el.trim().toLowerCase() == element.label.trim().toLowerCase() ||
                            el.trim().toLowerCase() == element.name.trim().toLowerCase(),
                    );
                    element.enable = !!data;
                }
                getRateMinMaxForLightreach(element, solarQuoteFormFieldTemp, skipAutoPriceUpdate);
            } else {
                retrieveSliderDetails(element, solarQuoteFormFieldTemp, pricingMethodOptions, skipAutoPriceUpdate);
            }
        } else if (pmVal != null && pmVal !== '') {
            if (resolvedPricingMethod?.Text) {
                element.label = resolvedPricingMethod.Text;
            }
            if(financialFieldsArr && financialFieldsArr.length > 0) {
                const labelToMatch = (element.label || '').trim().toLowerCase();
                const nameToMatch = (element.name || '').trim().toLowerCase();
                const data: any = financialFieldsArr.find(
                    (el) => el.trim().toLowerCase() === labelToMatch || el.trim().toLowerCase() === nameToMatch,
                );
                element.enable = !!data;
            }
            if (
                (resolvedPricingMethod?.PricingValue == 'EPC to Dealer' || resolvedPricingMethod?.Text == 'EPC') &&
                (Number(element?.value) || 0) < getDefaultPricingValue(true, solarQuoteFormFieldTemp)
            ) {
                element.message =
                    'The entered value should not be less than $' + getDefaultPricingValue(true, solarQuoteFormFieldTemp);
            } else if (
                (resolvedPricingMethod?.PricingValue == 'Dealer EPC Per Watt' ||
                    resolvedPricingMethod?.Text == 'Desired PPW (Net)') &&
                Number(element?.value) < 2.5
            ) {
                element.message = 'The entered value should not be less than $2.5';
            } else {
                element.message = '';
            }
            element.dataType = 'Amount';
            element.type = 'amount';
            element.range = undefined;
        }
    };

    /**
     * Builds the payload for `updateSolarDetails` from current form fields, React state, and `salesAmoontDetailsRef`.
     * Partner-specific rules pick PPW, pricingValue, TotalSaleAmount, etc. (e.g. Sunnova loan + Desired PPW uses ref totals).
     * `flag` is forwarded from GoodLeap flows where the caller needs to signal offer/total refresh semantics.
     */
    const updateSolarObject = (flag: any, currentFields?: FieldObject[]) => {
        const fieldsForValidation = currentFields || solarQuoteFormFields;
        let selectedPricingValue = getFieldDetails('PricingMethod', currentFields)?.options?.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        const SyncProductInfo = priceQuote.SyncProductInfo.find((element: any) => {
            return element.Product_Type_Name == 'Solar' && element.FinancePartner == confirmQuoteCellKey?.partner && element.FinanceMethodName == financeMethod
        });

        
        const lightreach = getLightreachDerived(currentFields);
        
        const lightreachFormInvalid = isLightreachPartner && lightreach.inEligibleLightreachQuote;

        const data: any = {
            opportunity_id: priceQuote.Opportunity_Id,
            // financePartnerId: solarPartner?.Value,
            financePartnerName: confirmQuoteCellKey?.partner || '',
            contract_Type: confirmQuoteCellKey?.methord || '',
            // term: 0,
            // apr: 0,
            epc: Number(getFieldDetails('EPC', currentFields)?.value),
            rate: Number(getFieldDetails('Rate', currentFields)?.value),
            ppw: ((confirmQuoteCellKey?.partner?.replace(" ", "").toLocaleLowerCase() == 'goodleap' || confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunlight')
                && financeMethod === 'Loan' && (selectedPricingValue?.Text == 'Desired PPW (Net)' || selectedPricingValue?.Text == 'EPC')) ?
                Number(salesAmoontDetailsRef.current?.GrossSalesPricePerWatt || grossSalesPricePerWatt) : (salesAmoontDetailsRef.current.PPW || PPW) !== null ? Number(salesAmoontDetailsRef.current.PPW || PPW) : Number(getFieldDetails('PPW', currentFields)?.value),
            pricingmethodName: selectedPricingValue?.Text || '',
            pricingmethod: selectedPricingValue?.PricingValue || '',
            pricingValue: (confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunnova'
                && SunnovaLoanOptions.indexOf(financeMethod.trim()) !== -1
                && selectedPricingValue?.Text == 'Desired PPW (Net)') ? (salesAmoontDetailsRef.current.TotalSaleAmount || totalSaleAmount) : Number(getFieldDetails('Price', currentFields)?.value),
            rebateMethod: getFieldDetails('RebateMethod', currentFields)?.value || '',
            rebate: Number(rebateAmount),
            Escalator: (getFieldDetails('Escalator', currentFields)?.value) ? getFieldDetails('Escalator', currentFields)?.value : 0,
            downPayment: Number(getFieldDetails('DownPayment', currentFields)?.value),
            tax: Number(getFieldDetails('Tax', currentFields)?.value),
            TermAndAPR: getFieldDetails('TermAPR', currentFields)?.value,
            design: getFieldDetails('DesignId', currentFields)?.value,
            PermitPayer: getFieldDetails('PermitPayer', currentFields)?.value,
            TotalSaleAmount: isLightreachPartner ? lightreach.lightreachTotalSaleAmount : (salesAmoontDetailsRef.current.TotalSaleAmount || totalSaleAmount),
            DesiredPPWNet: (confirmQuoteCellKey?.partner?.replace(" ", "").toLocaleLowerCase() === 'goodleap' || confirmQuoteCellKey?.partner?.toLocaleLowerCase() == 'sunlight') ? getDesiredPPWNet(currentFields) : getFieldDetails('DesiredPPWNet', currentFields)?.value,
            RebateAmountPerWatt: getFieldDetails('RebateMethod', currentFields)?.value == 'Per Watt' ? getFieldDetails('RebateValue', currentFields)?.value : 0,
            AdderPrice: getFieldDetails('AdderPrice', currentFields)?.value,
            AdderCost: getFieldDetails('AdderCost', currentFields)?.value,
            valid: (!!haveValidationErrors(fieldsForValidation) || lightreachFormInvalid) ? false : true,
            grossSalesPricePerWatt: salesAmoontDetailsRef.current?.GrossSalesPricePerWatt || grossSalesPricePerWatt,
            dealerFeePercentage: salesAmoontDetailsRef.current?.DealerFeePercentage || dealerFeePercentage,
            dealerEpc: salesAmoontDetailsRef.current?.DealerEpc || dealerEpc,
            Monthly_Finance_Payment_NoITCPayback: salesAmoontDetailsRef.current?.Monthly_Finance_Payment_NoITCPayback,
            Monthly_Finance_Partner_Payment: salesAmoontDetailsRef.current?.Monthly_Finance_Partner_Payment,
            ITC_Amount: salesAmoontDetailsRef.current?.ITCAmount,
            // SyncProductInfo: (SyncProductInfo)? SyncProductInfo : this.SyncProductInfo,
            Finance_Partner_Product_Id: getFieldDetails('Finance_Partner_Product_Id', currentFields)?.value,
            Offer_Id: isLightreachPartner ? lightreach.eligibleLightreachProductId : confirmQuoteCellKey?.payload?.offerId,
            IsACH: salesAmoontDetailsRef.current?.IsACH,
            Product_Type: ProductType,
            productionRate: salesAmoontDetailsRef.current?.ProductionRate || productionRate,
            SolarProductionRate: salesAmoontDetailsRef.current?.ProductionRate || productionRate,
            Deposit_Amount: getFieldDetails('Deposit', currentFields)?.value,
            Financed_Amount: salesAmoontDetailsRef.current.financedAmount || financedAmount,
            Total_Amount: isLightreachPartner ? lightreach.lightreachTotalSaleAmount : (salesAmoontDetailsRef.current.TotalSaleAmount || totalSaleAmount),
            UponCommencementOfPhysicalInstall: getFieldDetails('UponCommencementOfPhysicalInstall', currentFields)?.value,
            UponCompletionOfPhysicalInstall: getFieldDetails('UponCompletionOfPhysicalInstall', currentFields)?.value,
            UponCompletionOfTheContract: getFieldDetails('UponCompletionOfTheContract', currentFields)?.value,
            cloneQuoteId: confirmQuoteCellKey?.clonedQuoteDetails?.QuoteId
        };
        updateSolarDetails(data);
    }

    /** Resolves Desired PPW (net) for GoodLeap/Sunlight when it is not a raw form field for the current loan/pricing mode. */
    const getDesiredPPWNet = (currentFields?: FieldObject[]): any => {
        const selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        const selectedPricingText = selectedPricingValue?.Text;

        if (financeMethod?.includes('Loan')) {
            if (selectedPricingText === 'Desired PPW (Net)') {
                return getFieldDetails('Price', currentFields)?.value;
            } else if (selectedPricingText === 'EPC') {
                return grossSalesPricePerWatt;
            }
        } else if (financeMethod === 'Lease' || financeMethod == 'PPA') {
            return PPW;
        }
        return getFieldDetails('DesiredPPWNet', currentFields)?.value
    }

    /** GoodLeap: derives purchase price / PPW from partner API when not on loan path; otherwise defers to `updateTotalSaleAmount`. */
    const updateGoodLeapTotalSaleAmount = (flag?: any, offerId?: any, currentFields?: FieldObject[]) => {
        setGoodLeapValidationErrorMessage('');
        const selectedPricingValue = (getFieldDetails("PricingMethod", currentFields)?.options || [])?.find(a => a.Value == getFieldDetails('PricingMethod', currentFields)?.value);
        if (!priceQuote.ExcelUtilityCompanyName && isGoodLeap) setGoodLeapValidationErrorMessage('Utility information not saved');
        if (confirmQuoteCellKey?.methord === 'Loan' || confirmQuoteCellKey?.methord === 'Loan Storage') {
            updateTotalSaleAmount(flag);
        } else {
            let purchasePrice = 0, kWhRate = 0, dollarPerWatt = 0, monthlyPayment = 0;
            if (selectedPricingValue?.Text.trim() === 'Solar Rate') kWhRate = getFieldDetails('Price')?.value
            else if (selectedPricingValue?.Text.trim() === 'PPW') dollarPerWatt = getFieldDetails('Price')?.value
            else if (selectedPricingValue?.Text.trim() === 'Desired PPW (Net)') dollarPerWatt = getFieldDetails('Price')?.value
            else if (selectedPricingValue?.Text.trim() == 'Monthly Payment') monthlyPayment = getFieldDetails('Price')?.value
            else purchasePrice = getFieldDetails('Price')?.value

            if (purchasePrice != 0 || kWhRate != 0 || dollarPerWatt != 0 || monthlyPayment != 0) {
                let payload = {
                    opportunityId: priceQuote.Opportunity_Id,
                    offerId: offerId,
                    systemSize: priceQuote.EPCCalc.SystemSizeValue,
                    estimatedAnnualProduction: ((priceQuote.EPCCalc.ProductionSizeValue ?? Number(priceQuote.EPCCalc.ProductionSizeValue))) * 1000,
                    kWhRate: kWhRate,
                    monthlyPayment: monthlyPayment,
                    dollarPerWatt: dollarPerWatt,
                    purchasePrice: purchasePrice
                }
                updateLoaderCount(true);
                updateErrorMessage('');
                quoteServices.updateGoodLeapTotalSaleAmount(payload).then((res: any) => {
                    salesAmoontDetailsRef.current = {
                        TotalSaleAmount: res.purchasePrice,
                        Monthly_Finance_Partner_Payment: res.firstYearMonthlyPayment,
                        PPW: res.PPW,
                        ProductionRate: res.productionRate,
                    }
                    setTotalSaleAmount(res.purchasePrice);
                    setMonthly_Finance_Partner_Payment(res.firstYearMonthlyPayment);
                    setPPW(res.PPW);
                    setGoodLeapValidationErrorMessage(res.description);
                    setProductionRate(res.productionRate);
                    updateLoaderCount(false);
                    updateSolarObject(flag, currentFields);
                    if (res.description){
                        updateErrorMessage(res.description);
                    }
                }).catch(() => {
                    updateLoaderCount(false);
                });
            } else {
                setTotalSaleAmount(0);
                updateSolarObject(false, currentFields);
            }
        }
    }

    const goodLeapDropDownChange = (flag?: any) => {
        const selectedPricingValue = (solarQuoteFormFields.find(a => a.name == 'PricingMethod')?.options || []).find(a => a.Value == getFieldDetails('PricingMethod')?.value);
        if (selectedPricingValue && selectedPricingValue.Text) {
            const FinancialData = {
                "ExcelFileName": "Sunnova Financial Data",
                "SheetName": "SVA Financing Options",
                "InputValues": [
                    { "Type": "String", "CellNumber": "B4", "CellValue": priceQuote.InstallationState },
                    { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4() },
                    { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
                    { "Type": "String", "CellNumber": "F4", "CellValue": confirmQuoteCellKey?.methord },
                    { "Type": "String", "CellNumber": "K4", "CellValue": selectedPricingValue?.Text },
                    { "Type": "String", "CellNumber": "M4", "CellValue": getFieldDetails('Escalator')?.value },
                    { "Type": "String", "CellNumber": "O4", "CellValue": getFieldDetails('TermAPR')?.value?.trim() },
                    { "Type": "String", "CellNumber": "E8", "CellValue": priceQuote.UtilityCompanyName || '' },
                ],
                "OutputCells": ["T4", "U11"],
            };
            updateLoaderCount(true);
            masterServices.cascadingDropDown(FinancialData).then((data: ApiResponse) => {
                let offerId = ''
                setFinance_Partner_Product_Id(data[1].CellValue);
                if (existingOfferIdDetails && existingOfferIdDetails.FinanceMethod == financeMethod
                    && ((financeMethod.includes('Loan') && getFieldDetails('TermAPR')?.value?.trim() == existingOfferIdDetails.TermAPR.trim())
                        || (!financeMethod.includes('Loan') && existingOfferIdDetails.Escalator == getFieldDetails('Escalator')?.value
                            && !existingOfferIdDetails.isUtilityUpdated))) {
                    offerId = existingOfferIdDetails.OfferId;
                    if(confirmQuoteCellKey) {
                        confirmQuoteCellKey.payload.offerId = existingOfferIdDetails.OfferId;
                    }
                    setOffer_Id(existingOfferIdDetails.OfferId)
                } else {
                    if(!!confirmQuoteCellKey) {
                        if(confirmQuoteCellKey.payload) confirmQuoteCellKey.payload.offerId = data[0].CellValue;
                        else confirmQuoteCellKey.payload = { offerId: data[0].CellValue };
                    }
                    setOffer_Id(data[0].CellValue);
                    offerId = data[0].CellValue;
                }
                updateLoaderCount(false);
                updateGoodLeapTotalSaleAmount(flag, offerId, solarQuoteFormFields);
            }).catch(() => {
                updateLoaderCount(false);
            });
        }
    }

    const getSunlightPartnerTotalSaleAmount = () => {
        const selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod')?.value);
        if (financeMethod === 'Loan' && selectedPricingValue?.Text === 'EPC') {
            setTotalSaleAmount(getFieldDetails('Price')?.value);
            setFinancedAmount((getFieldDetails('Deposit_Amount')?.value) ? totalSaleAmount - Number(getFieldDetails('Deposit_Amount')?.value) : totalSaleAmount)
            updateSolarObject(false);
        }
        if (selectedPricingValue?.Text === 'Desired PPW (Net)' || selectedPricingValue?.Text === 'EPC') {
            updateTotalSaleAmount(false);
            updateSolarObject(false);
        }
    }

    const getSunlightPartnerValues = () => {
        const selectedPricingValue = pricingMethodArr.find(a => a.Value == getFieldDetails('PricingMethod')?.value);
        const financialData = {
            "ExcelFileName": "Sunnova Financial Data",
            "SheetName": "SVA Financing Options",
            "InputValues": [
                { "Type": "String", "CellNumber": "B4", "CellValue": priceQuote.InstallationState },
                { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4() },
                { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
                { "Type": "String", "CellNumber": "F4", "CellValue": financeMethod },
                { "Type": "String", "CellNumber": "K4", "CellValue": selectedPricingValue?.Text },
                { "Type": "String", "CellNumber": "O4", "CellValue": getFieldDetails('TermAPR')?.value?.trim() }
            ],
            "OutputCells": ["V11", "W11"],
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(financialData).then((data: ApiResponse) => {
            setIsACH(data[0].CellValue);
            setProductType(data[1].CellValue);
            updateLoaderCount(false);
            getSunlightPartnerTotalSaleAmount();
        }).catch(() => {
            updateLoaderCount(false);
        });
    }


    const updateOtherFields = (solarQuoteFormFieldsTemp: any[], financialArr: any[]) => {
        if(financialArr.find(field => field.trim().toLocaleLowerCase() == 'downpayment')) {
            setSolarQuoteFromValues('RebateMethod', rebateMethods[0]?.Value);
        } 
        const pricingMethodArr = getFieldDetails('PricingMethod')?.options || [];
        const escalatorArr = getFieldDetails('Escalator')?.options || [];
        const termAPRArr = getFieldDetails('TermAPR')?.options || [];
        const downPaymentList = getFieldDetails('DownPayment')?.options || [];  
        // let solarQuoteFormFieldTemp = [...solarQuoteFormFieldsTemp];

        switch (confirmQuoteCellKey?.methord) {
            case 'PPA-EZ':
            case 'Prepaid PPA-EZ':
                if (pricingMethodArr.length > 0) setSolarQuoteFromValues('PricingMethod', '0');
                if (localStorage.getItem('PPA_Requested_Quote_Created') != 'Not Interested') {
                    if (priceQuote.RequestedPPARateEscalator && escalatorArr.length > 0) {
                        let esc = priceQuote.RequestedPPARateEscalator.replace(/%/g, '');
                        if (esc == '0.0' || esc == '0.00') esc = '0'
                        let result = escalatorArr.filter((elem: any) => elem.Value.trim() == esc);
                        const Escalator = confirmQuoteCellKey?.ExcelOffer?.escalator ?? result[0].Value;
                        setSolarQuoteFromValues('Escalator', Escalator);
                    }
                    if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                        const PricingMethod = getFieldDetails('PricingMethod')?.value;
                        let price = priceQuote?.ProposedRate || 0;
                        if (PricingMethod?.Text == 'Solar Rate') price = confirmQuoteCellKey?.ExcelOffer.startingRate || 0;
                        setSolarQuoteFromValues('Price', Number(priceQuote.RequestedPPARate) ? Number(priceQuote.RequestedPPARate) : getDefaultPricingValue());
                    }
                } else {
                    const Escalator = confirmQuoteCellKey?.ExcelOffer?.escalator ?? escalatorArr[0].Value;
                    if (escalatorArr.length > 0) setSolarQuoteFromValues('Escalator', Escalator);
                    if (!confirmQuoteCellKey?.clonedQuoteDetails) setSolarQuoteFromValues('Price', getDefaultPricingValue());
                }
                break;
            case 'Lease':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    const Escalator = confirmQuoteCellKey?.ExcelOffer?.escalator ?? escalatorArr[0].Value;
                    if (pricingMethodArr.length > 0) setSolarQuoteFromValues('PricingMethod', pricingMethodArr[0].Value);
                    if (escalatorArr.length > 0) setSolarQuoteFromValues('Escalator', Escalator);
                    if (termAPRArr.length > 0) setSolarQuoteFromValues('TermAPR', termAPRArr[0].Value);
                    if (downPaymentList.length > 0) setSolarQuoteFromValues('DownPayment', downPaymentList[0].Value);
                } 
                break;
            case 'Loan':
            case 'Loan Storage':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    const Escalator = confirmQuoteCellKey?.ExcelOffer?.escalator ?? escalatorArr[0].Value;
                    if (pricingMethodArr.length > 0) setSolarQuoteFromValues('PricingMethod', pricingMethodArr[0].Value);
                    if (escalatorArr.length > 0) setSolarQuoteFromValues('Escalator', Escalator);
                    if (termAPRArr.length > 0) setSolarQuoteFromValues('TermAPR', termAPRArr[0].Value);
                    if (downPaymentList.length > 0) setSolarQuoteFromValues('DownPayment', downPaymentList[0].Value);
                    setSolarQuoteFromValues('Price', getDefaultPricingValue());
                } 
                if ((getFieldDetails('PricingMethod')?.value != null || getFieldDetails('PricingMethod')?.value != undefined || getFieldDetails('PricingMethod')?.value != '') && getFieldDetails('Price')?.enable) {
                    const PricingMethod: any = pricingMethodArr.find(ele => ele.Value == getFieldDetails('PricingMethod')?.value);
                    solarQuoteFormFieldsTemp.forEach(ele => {
                        if (ele.name == 'Price') ele.label = PricingMethod.Text;
                    });
                    setsolarQuoteFormFields(solarQuoteFormFieldsTemp);
                }
                break;
            case 'PPA':
            case 'PPA Storage':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) { 
                    const escalator = confirmQuoteCellKey?.ExcelOffer?.escalator;
                    if (pricingMethodArr.length > 0) setSolarQuoteFromValues('PricingMethod', pricingMethodArr[0]?.Value);
                    if (escalatorArr.length > 0) setSolarQuoteFromValues('Escalator', escalator ?? escalatorArr[0].Value);
                    if (termAPRArr.length > 0 && termAPRArr[0].Value) setSolarQuoteFromValues('TermAPR', termAPRArr[0].Value);
                    if (downPaymentList.length > 0) setSolarQuoteFromValues('DownPayment', downPaymentList[0].Value);
                } 
                break;
            case 'Cash Sale':
            case 'Cash Contract':
                if (pricingMethodArr.length > 0) setSolarQuoteFromValues('PricingMethod', pricingMethodArr.find(a => a.Text === 'Desired PPW (Net)')?.Value);
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    if (termAPRArr.length > 0) setSolarQuoteFromValues('TermAPR', termAPRArr[0].Value);
                    if (downPaymentList.length > 0) setSolarQuoteFromValues('DownPayment', downPaymentList[0].Value);
                } 
                break;
            case 'Lease Storage':
                if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                    if (pricingMethodArr.length > 0) setSolarQuoteFromValues('PricingMethod', pricingMethodArr[0].Value);
                    if (escalatorArr.length > 0) setSolarQuoteFromValues('Escalator', escalatorArr[0].Value);
                } 
                break;
        }

        /**
         * Mirror switch() + GoodLeap inits on `solarQuoteFormFieldTemp` in the same tick. The switch only
         * calls setSolarQuoteFromValues (async for this closure); without this, PricingMethod/Price/label
         * stay stale on the first pass and EPC can miss render/validation (logs: entry pmState "" vs
         * commit EPC); see H1-H3.
         */
        if (!confirmQuoteCellKey?.clonedQuoteDetails) {
            if (confirmQuoteCellKey?.methord === 'Loan' || confirmQuoteCellKey?.methord === 'Loan Storage') {
                if (pricingMethodArr.length > 0) {
                    const f = solarQuoteFormFieldsTemp.find((e) => e.name === 'PricingMethod');
                    if (f) f.value = pricingMethodArr[0].Value;
                }
                if (escalatorArr.length > 0) {
                    const f = solarQuoteFormFieldsTemp.find((e) => e.name === 'Escalator');
                    if (f) f.value = escalatorArr[0].Value;
                }
                if (termAPRArr.length > 0) {
                    const f = solarQuoteFormFieldsTemp.find((e) => e.name === 'TermAPR');
                    if (f) f.value = termAPRArr[0].Value;
                }
                if (downPaymentList.length > 0) {
                    const f = solarQuoteFormFieldsTemp.find((e) => e.name === 'DownPayment');
                    if (f) f.value = downPaymentList[0].Value;
                }
            }
            if (isGoodLeap) {
                const glProductName = lineMode === 'battery' ? 'Battery' : 'Solar';
                const financeRquest = priceQuote.FinanceRquestOptions.find(
                    (ele: any) =>
                        String(ele?.RequestedPartner ?? '')
                            .toLowerCase() === 'goodleap' && ele.Product_Type_Name == glProductName,
                );
                const pm =
                    financeRquest?.Pricing_Method != null ? financeRquest.Pricing_Method : pricingMethodArr[0]?.Value;
                if (pm != null) {
                    const f = solarQuoteFormFieldsTemp.find((e) => e.name === 'PricingMethod');
                    if (f) f.value = pm;
                }
            }
            if (confirmQuoteCellKey?.methord === 'Loan' || confirmQuoteCellKey?.methord === 'Loan Storage') {
                const p = solarQuoteFormFieldsTemp.find((e) => e.name === 'Price');
                if (p) p.value = getDefaultPricingValue(false, solarQuoteFormFieldsTemp);
            }
        }

        const PricingMethod = pricingMethodArr.find(
            (a) => a.Value == getFieldDetails('PricingMethod', solarQuoteFormFieldsTemp)?.value,
        );
        // applyPriceFieldFromPricingMethod(solarQuoteFormFieldsTemp, financialArr);
        solarQuoteFormFieldsTemp.forEach(element => {
            if ((confirmQuoteCellKey?.methord === 'PPA' || confirmQuoteCellKey?.methord === 'PPA Storage') && element.name === 'Price' && !confirmQuoteCellKey?.clonedQuoteDetails) {
                let price = priceQuote?.ProposedRate || 0;
                if (PricingMethod?.Text == 'Solar Rate') price = confirmQuoteCellKey?.ExcelOffer.startingRate || 0;
                setSolarQuoteFromValues(element.name, Number(price));
            }
        });

        if (confirmQuoteCellKey?.partner == 'Cash') {
            updateTotalSaleAmount(false);
        }
        if (isGoodLeap) {
            const glProductName = lineMode === 'battery' ? 'Battery' : 'Solar';
            let financeRquest = priceQuote.FinanceRquestOptions.find(
                (ele: any) => ele.RequestedPartner.toLocaleLowerCase() == 'goodleap' && ele.Product_Type_Name == glProductName,
            );
            if (!confirmQuoteCellKey?.clonedQuoteDetails) {
                setSolarQuoteFromValues('PricingMethod', (financeRquest?.Pricing_Method) ? financeRquest?.Pricing_Method : pricingMethodArr[0].Value);
                if (!confirmQuoteCellKey?.ExcelOffer?.escalator && priceQuote.RequestedPPARateEscalator){
                    let esc = priceQuote.RequestedPPARateEscalator.replace(/%/g, '');
                    if(esc == '0.0' || esc == '0.00') esc = '0'
                    let result = escalatorArr.find((elem: any) => elem.Value.trim() == esc);
                    if (!result) setSolarQuoteFromValues('Escalator', escalatorArr[0].Value);
                    else setSolarQuoteFromValues('Escalator', priceQuote.RequestedPPARateEscalator.replace('%', ''));
                } 
                else {
                    if(confirmQuoteCellKey?.ExcelOffer?.offerType)
                        setSolarQuoteFromValues('TermAPR', confirmQuoteCellKey?.ExcelOffer?.offerType);
                }
            }
            if (confirmQuoteCellKey?.payload?.offerId) {
                let offerId = ''
                if (existingOfferIdDetails && existingOfferIdDetails.FinanceMethod == financeMethod
                    && ((financeMethod.includes('Loan') && getFieldDetails('TermAPR')?.value?.trim() == existingOfferIdDetails.TermAPR.trim())
                        || (!financeMethod.includes('Loan') && existingOfferIdDetails.Escalator == getFieldDetails('Escalator')?.value
                            && !existingOfferIdDetails.isUtilityUpdated))) {
                    offerId = existingOfferIdDetails.OfferId;
                    if(confirmQuoteCellKey) {
                        confirmQuoteCellKey.payload.offerId = existingOfferIdDetails.OfferId;
                    }
                    setOffer_Id(existingOfferIdDetails.OfferId)
                } else {
                    setOffer_Id(confirmQuoteCellKey?.payload?.offerId);
                    offerId = confirmQuoteCellKey?.payload?.offerId;
                }
                updateGoodLeapTotalSaleAmount(false, offerId);
            } else {
                goodLeapDropDownChange();
            }

        } else if (confirmQuoteCellKey?.partner == 'Sunlight') getSunlightPartnerValues();
        else updateSolarObject(false, solarQuoteFormFieldsTemp);
        
        solarQuoteFormFieldsTemp.forEach((ele: any) => {
            if (ele.enable && getFieldDetails(ele?.field)?.value == null && ele.dataType == 'Textbox') {
                setSolarQuoteFromValues(ele.field, 0);
            }
        });
        const desiredPpwFieldTemp = solarQuoteFormFieldsTemp.find((x) => x.name === 'DesiredPPWNet');
        if (desiredPpwFieldTemp?.enable) {
            const desiredValue = Number(desiredPpwFieldTemp.value ?? 0);
            desiredPpwFieldTemp.message =
                desiredValue < 2.5 ? 'The entered value should not be less than $2.5' : '';
        }
        setsolarQuoteFormFields(solarQuoteFormFieldsTemp);
    }

    const getTermAPR = (PricingMethod: any, data: any) => {
        let result = '';
        if (String(PricingMethod).includes('Loan'))
          result =
            (data.Term && data.Term != '' ? data.Term : 0) +
            ' years at '+
            (data.TermAPR && data.TermAPR != ''
              ? Number(data.TermAPR).toFixed(2)
              : 0) +
            '%';
        if (result == '')
          result =
            (data.TermAPR && data.TermAPR != ''
              ? Number(data.TermAPR).toFixed(1)
              : 0) + '%';
        return result;
      }

    const getSolarQuoteFormValues = (fm: string) => {
        if (!priceQuote) return;
        const installationState = priceQuote?.InstallationState ?? '';
        const excelUtilityCompanyName = priceQuote?.ExcelUtilityCompanyName ?? '';

        let selectedPricingValue = null;
        if (getFieldDetails('PricingMethod')?.enable) {
            selectedPricingValue = pricingMethodArr.find(a => (a.Value == getFieldDetails('PricingMethod')?.value || a.Text == getFieldDetails('PricingMethod')?.value));
        }
        const data = {
            "ExcelFileName": "Sunnova Financial Data",
            "SheetName": "SVA Financing Options",
            "InputValues": [{
                "Type": "String", "CellNumber": "B4", "CellValue": installationState
            },
            { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4(fm ?? financeMethod) },
            { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
            { "Type": "String", "CellNumber": "F4", "CellValue": fm ?? financeMethod },
            { "Type": "String", "CellNumber": "C8", "CellValue": eligibleProduct },
            { "Type": "String", "CellNumber": "E8", "CellValue": excelUtilityCompanyName },
            { "Type": "String", "CellNumber": "K4", "CellValue": selectedPricingValue?.Text }
            ], "OutputCells": ["G4", "N4", "H4", "L4", "I4", "G8", "T4", 'N11'],
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(data).then((response: ApiResponse) => {
            let fields: string[] = response[0].CellValue.split('|');
            setFinancialArr(fields);
            fields.forEach((field: string) => enableFields(field));

            let termAPRArrTemp: any[] = []
            let TermAPRArr: string[] = response[1].CellValue.split('|');
            let suffix = (confirmQuoteCellKey?.partner == 'Sunlight') ? ' w/ ACH' : '';
            TermAPRArr.forEach(ele => termAPRArrTemp.push({ Value: ele.trim() + suffix, Text: ele.trim() + suffix }));

            if (confirmQuoteCellKey?.partner == 'Sunlight') {
                let ACHTermAPRArr: string[] = response[7].CellValue.split('|');
                ACHTermAPRArr.forEach(ele => {
                    if (ele) termAPRArrTemp.push({ Value: ele.trim() + ' w/o ACH', Text: ele.trim() + ' w/o ACH' });
                });
            }
            if (confirmQuoteCellKey?.clonedQuoteDetails) {
                const clonedTermAPR = getFieldDetails('TermAPR')?.value;
                if (clonedTermAPR && !termAPRArrTemp.find(x => x.Value == clonedTermAPR)) termAPRArrTemp.push({ Value: clonedTermAPR, Text: clonedTermAPR });
            }
            if(fields.find(field => field.trim().toLocaleLowerCase() == 'downpayment')) {
                getDownpaymentList(financeMethod)
            } 

            let PricingMethodArr: string[] = response[2].CellValue.split('|');
            let PricingMethodvalArr: string[] = (response[4].CellValue.length > 0) ? response[4].CellValue.split('|') : [];
            let pricingMethods: any[] = [];
            for (let i = 0; i < PricingMethodArr.length; i++) {
                pricingMethods.push({
                    PricingValue: (PricingMethodvalArr[i]) ? PricingMethodvalArr[i].trim() : PricingMethodArr[i].trim(),
                    Text: PricingMethodArr[i].trim(),
                    Value: i+1
                })
            }
            setPricingMethodArr(pricingMethods);
            let escalatorArrTemp: any = [];
            let EscalatorArr: string[] = response[3].CellValue.split('|');
            EscalatorArr.forEach((ele) => escalatorArrTemp.push({ Value: ele.trim(), Text: ele.trim() }));
            if (escalatorArrTemp.length > 0) setEscolatorRange(escalatorArrTemp);
            let rebateMethods: string[] = response[5].CellValue.split('|');
            let rebateMethodsList: any[] = []
            rebateMethods.forEach((ele) => rebateMethodsList.push({ Value: ele.trim(), Text: ele.trim() }));

            let solarQuoteFormFieldsTemp = [...solarQuoteFormFields];
            solarQuoteFormFieldsTemp.forEach(ele => {
                if (ele.name == "TermAPR") ele.options = termAPRArrTemp
                if (ele.name == "PricingMethod") ele.options = pricingMethods
                if (ele.name == "Escalator") ele.options = escalatorArrTemp
                if (ele.name == "RebateMethod") ele.options = rebateMethodsList
            });

            if(confirmQuoteCellKey?.clonedQuoteDetails) {
                solarQuoteFormFieldsTemp.forEach(ele => {
                    if(ele.enable){
                        switch(ele.name){
                            case "TermAPR":
                                let Term = getTermAPR(confirmQuoteCellKey?.clonedQuoteDetails.Quote_FinanceMethod,
                                    {Term:confirmQuoteCellKey?.clonedQuoteDetails.Term,TermAPR:confirmQuoteCellKey?.clonedQuoteDetails.TermAPR})
                                // if (financePartner?.Text === ) {
                                //         Term = Term + ' w/ ACH';
                                // }
                                ele.value = String(Term);
                                break;
                            case "PricingMethod":
                                    const PricingMethod = confirmQuoteCellKey?.clonedQuoteDetails.Quote_PricingMethod;
                                    const PricingMethodId = pricingMethods.find(ele => ele.Text == PricingMethod)?.Value
                                    if (pricingMethods.length > 0) ele.value = PricingMethodId;
                                    else ele.value = ""
                                    applyPriceFieldFromPricingMethod(solarQuoteFormFieldsTemp, financialArr);
                                break;
                            case "Price":
                                if(confirmQuoteCellKey?.clonedQuoteDetails.Quote_PricingMethod == 'EPC')
                                    ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.PricingValue)?Number(confirmQuoteCellKey?.clonedQuoteDetails.PricingValue):0);
                                else if(confirmQuoteCellKey?.clonedQuoteDetails.Quote_PricingMethod == 'Desired PPW (Net)'){
                                    ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.Desired_PPW_Net);
                                } else ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Solar_Rate)?confirmQuoteCellKey?.clonedQuoteDetails.Solar_Rate:0);
                                break;        
                            case "Escalator":
                                    ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Escalator)?confirmQuoteCellKey?.clonedQuoteDetails.Escalator:0);
                                    applyPriceFieldFromPricingMethod(solarQuoteFormFieldsTemp, financialArr);
                                break;
                            // case "Rate":

                            // case "EPC":

                            // case "PPW":

                            case "DownPayment":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.DownPayment)?confirmQuoteCellKey?.clonedQuoteDetails.DownPayment:0);
                                break;
                            case "DesiredPPWNet":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.Desired_PPW_Net);
                                break;
                            case "Deposit":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.Deposit_Amount);
                                break;
                            case "RebateMethod":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Rebate_Method)?confirmQuoteCellKey?.clonedQuoteDetails.Rebate_Method:"");
                                break;
                            case "Tax":

                            case "AdderCost":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Apr)?confirmQuoteCellKey?.clonedQuoteDetails.Apr:0);
                                break;
                            case "AdderPrice":
                                ele.value = String((confirmQuoteCellKey?.clonedQuoteDetails.Adder_Price)?confirmQuoteCellKey?.clonedQuoteDetails.Adder_Price:0);
                                break;
                            case "UponCommencementOfPhysicalInstall":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.AmountOnPhysicalInstallStart);
                                break;
                            case "UponCompletionOfPhysicalInstall":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.AmountOnPhysicalInstallCompletion);
                                break;
                            case "UponCompletionOfTheContract":
                                ele.value = String(confirmQuoteCellKey?.clonedQuoteDetails.AmountOnContractCompletion);
                                break;
                            default:
                                break;
                        }
                    }
                })
            }

            setsolarQuoteFormFields(solarQuoteFormFieldsTemp);
            updateLoaderCount(false);
            updateOtherFields(solarQuoteFormFieldsTemp,fields);
        }).catch(() => {
            updateLoaderCount(false);
        });
    }


    const getFinanceMethod = () => {
        if (!priceQuote) return;
        const installationState = priceQuote?.InstallationState ?? '';
        const excelUtilityCompanyName = priceQuote?.ExcelUtilityCompanyName ?? '';
        if (confirmQuoteCellKey?.partner === 'Cash') {
            enableFields('Upon Commencement of Physical Install');
            enableFields('Upon Completion of Physical Install');
            enableFields('Upon Completion of the Contract');
        }
        const data = {
            "ExcelFileName": "Sunnova Financial Data",
            "SheetName": "SVA Financing Options",
            "InputValues": [{
                "Type": "String", "CellNumber": "B4", "CellValue": installationState
            },
            { "Type": "String", "CellNumber": "C4", "CellValue": getSvaC4(confirmQuoteCellKey?.methord as string) },
            { "Type": "String", "CellNumber": "D4", "CellValue": (confirmQuoteCellKey?.partner) ? getExcelFinancePartner(confirmQuoteCellKey?.partner) : '' },
            { "Type": "String", "CellNumber": "C8", "CellValue": eligibleProduct },
            { "Type": "String", "CellNumber": "E8", "CellValue": excelUtilityCompanyName }
            ], "OutputCells": ["E4"]
        };
        updateLoaderCount(true);
        masterServices.cascadingDropDown(data).then((response: ApiResponse) => {
            setFinanceMethod(response[0].CellValue);
            if (confirmQuoteCellKey) confirmQuoteCellKey.methord = response[0].CellValue;
            getSolarQuoteFormValues(response[0].CellValue);
            updateLoaderCount(false);
        }).catch(() => {
            updateLoaderCount(false);
        });
    }


    const getSliderRateNote =  (field: any): string => {
        if (field?.name !== 'Price' || !field?.range?.nzValue) return '';
        const currentRate = Number(getFieldDetails(field.name)?.value);
        const targetRate = Number(field.range.nzValue);
        const escalationRate = field.range.escalationRate || getFieldDetails('Escalator')?.value;
        if (isNaN(currentRate) || isNaN(targetRate)) return '';

        if (targetRate > field.range.nzMax) {
          return ` Starting rate is out of range of the pricing the finance partner allows. Please adjust the utility information or switch finance partners.`;
        }
        if (currentRate === targetRate) {
          return `current rate matches target rate of <b>$${targetRate}</b> at <b>${escalationRate}% escalation rate</b>`;
        }
        const direction = currentRate < targetRate ? 'below' : 'At or above';
        return ` current rate ${direction} target rate of <b>$${targetRate}</b> at <b>${escalationRate}% escalation rate</b>`;
    }

    const getSliderRateNoteColor = (field: any): string  => {
        if (field?.name !== 'Price' || !field?.range?.nzValue) return '';
        const currentRate = Number(getFieldDetails(field.name)?.value);
        const targetRate = Number(field.range.nzValue);
        if (isNaN(currentRate) || isNaN(targetRate)) return '';
     
        return currentRate < targetRate ? '#d4a900' : 'green';
    }

    useEffect(() => {
        if (!confirmQuoteCellKey) return;
        const columnOk =
            lineMode === 'battery'
                ? columnIncludesBattery(confirmQuoteCellKey.product)
                : columnIncludesSolar(confirmQuoteCellKey.product);
        if (!columnOk) return;
        amountDraftRef.current = {};
        setsolarQuoteFormFields(JSON.parse(JSON.stringify(solarFields)));

        const partner = confirmQuoteCellKey.partner || '';
        const isSunnova = /^sunnova$/i.test(partner.trim());

        if (lineMode === 'battery' && isSunnova) {
            const c4Batt = batteryCalculatorC4FromColumn(confirmQuoteCellKey.product);
            const battPayload = {
                ExcelFileName: 'Battery Calculator',
                SheetName: 'Battery IO',
                InputValues: [
                    { Type: 'String', CellNumber: 'C3', CellValue: priceQuote?.InstallationState ?? '' },
                    { Type: 'String', CellNumber: 'C4', CellValue: c4Batt },
                    { Type: 'String', CellNumber: 'C7', CellValue: getExcelFinancePartner(partner) },
                ],
                OutputCells: ['F14', 'F15'],
            };
            masterServices.cascadingDropDown(battPayload).then((out: ApiResponse) => {
                if (!out?.[0]) return;
                const methods = String(out[0].CellValue || '')
                    .split('|')
                    .map((s) => s.trim())
                    .filter(Boolean);
                const fromGrid = (confirmQuoteCellKey?.methord || '').trim();
                const meth =
                    fromGrid && methods.some((m) => m === fromGrid)
                        ? fromGrid
                        : methods[0] || fromGrid;
                setFinanceMethod(meth);
                getSolarQuoteFormValues(meth);
            }).catch(() => { });
            return;
        }

        if (!confirmQuoteCellKey?.methord || confirmQuoteCellKey.methord == 'CASH') getFinanceMethod();
        else {
            if (confirmQuoteCellKey?.partner === 'Cash') {
                enableFields('Upon Commencement of Physical Install');
                enableFields('Upon Completion of Physical Install');
                enableFields('Upon Completion of the Contract');
            }
            setFinanceMethod(confirmQuoteCellKey?.methord);
            getSolarQuoteFormValues(confirmQuoteCellKey?.methord);
        }
    }, [confirmQuoteCellKey, lineMode, lightreachEligibleProductData]);

    /**
     * When EPC min is (system kW * 1000 * 2.5), the first `updateOtherFields` may run before `EPCCalc.SystemSizeValue`
     * is populated, so min is 0 and the Price under-min message is skipped. Re-validate when system size lands.
     */
    useEffect(() => {
        if (!isGoodLeap) return;
        const sys = Number(priceQuote?.EPCCalc?.SystemSizeValue) || 0;
        if (sys <= 0) return;
        setsolarQuoteFormFields((prev) => {
            const minEpc = getDefaultPricingValue(true, prev);
            if (minEpc <= 0) return prev;
            const selected = pricingMethodArr.find(
                (a) => a.Value == prev.find((f) => f.name === 'PricingMethod')?.value,
            );
            if (!selected) return prev;
            const isEpc = selected.PricingValue === 'EPC to Dealer' || selected.Text === 'EPC';
            if (!isEpc) return prev;
            const priceEl = prev.find((f) => f.name === 'Price');
            if (!priceEl?.enable) return prev;
            const priceNum = Number(priceEl.value) || 0;
            return prev.map((el) => {
                if (el.name !== 'Price') return el;
                if (priceNum < minEpc) {
                    return { ...el, message: 'The entered value should not be less than $' + minEpc };
                }
                if (el.message?.startsWith('The entered value should not be less than $')) {
                    return { ...el, message: '' };
                }
                return el;
            });
        });
    }, [isGoodLeap, confirmQuoteCellKey?.clonedQuoteDetails, priceQuote?.EPCCalc?.SystemSizeValue, pricingMethodArr]);

    const downPaymentField = getFieldDetails('DownPayment');
    const desiredPpwField = getFieldDetails('DesiredPPWNet');
    useEffect(() => {
        if (!desiredPpwField?.enable) return;
        const message = 'The entered value should not be less than $2.5';
        const desiredValue = Number(desiredPpwField.value ?? 0);
        const nextMessage = desiredValue < 2.5 ? message : '';
        if ((desiredPpwField.message ?? '') === nextMessage) return;
        let changed = false;
        const nextFields = solarQuoteFormFields.map((el) => {
            if (el.name !== 'DesiredPPWNet') return el;
            const current = el.message ?? '';
            if (current === nextMessage) return el;
            changed = true;
            return { ...el, message: nextMessage };
        });
        if (!changed) return;
        setsolarQuoteFormFields(nextFields);
        updateSolarObject(false, nextFields);
    }, [desiredPpwField?.enable, desiredPpwField?.value, desiredPpwField?.message, solarQuoteFormFields]);
    const downPaymentUsesOptions =
        !!downPaymentField?.options &&
        Array.isArray(downPaymentField.options) &&
        downPaymentField.options.length > 0;

    /** Good Leap / Sunlight: desired PPW (net) and down payment are pipeline-driven — hide rail editors; keep in main grid when enabled. */
    const solarRailHideDownPaymentAndDesiredPpw =
        lineMode === 'solar' && (partnerNorm === 'goodleap' || partnerNorm === 'sunlight');

    const sectionTitle = lineMode === 'battery' ? 'Battery' : 'Solar';
    const solarHeaderStats: GqSectionStat[] = [];
    /** Angular `solar-detail-form`: LightReach `app-solar-total` binds `[TotalSaleAmount]="lightreachTotalSaleAmount"`. */
    const headerGrossOrTotalAmount = isLightreachPartner ? lightreachDerived.lightreachTotalSaleAmount : totalSaleAmount;
    if (headerGrossOrTotalAmount > 0) {
        if (confirmQuoteCellKey?.partner == 'Sunnova') {
            solarHeaderStats.push({
                label: 'Total sales amount',
                value: `$${Number(headerGrossOrTotalAmount).toFixed(2)}`,
            });
        } else {
            solarHeaderStats.push({
                label: 'Gross sales price',
                value: `$${Number(headerGrossOrTotalAmount).toFixed(2)}`,
                emphasize: true,
            });
        }
        // if (isLightreachPartner && lightreachDerived.eligibleLightreachProductId) {
        //     solarHeaderStats.push({
        //         label: 'Offer ID',
        //         value: lightreachDerived.eligibleLightreachProductId,
        //     });
        // }
        if (financeMethod.includes('Loan')) {
            solarHeaderStats.push({
                label: 'Financed amount',
                value: `$${Number(financedAmount).toFixed(2)}`,
            });
        }
    } else {
        if(confirmQuoteCellKey?.partner != 'IGS') {
            solarHeaderStats.push({ label: 'Totals', value: 'Pending' });
        }
    }

    const selectedPricingMethod = pricingMethodArr.find(
        (p) => p.Value == getFieldDetails('PricingMethod')?.value,
    );
    const normalizedPricingMethodLabel = (selectedPricingMethod?.Text ?? '').trim().toLowerCase();
    const priceDecimalsLimit = PRICING_METHOD_LABELS_EXTRA_PRICE_DECIMALS.includes(
        normalizedPricingMethodLabel,
    )
        ? 3
        : DEFAULT_AMOUNT_DECIMALS_LIMIT;

    const pricingMethodDisplayText = (selectedPricingMethod?.Text ?? '').trim();
    const priceAdjustmentSectionLabel =
        pricingMethodDisplayText.length > 0 ? `Adjustments ${pricingMethodDisplayText}` : 'Adjustments';

    return (
        <div className="obd-modal__body obd-roof-quote-details__container--split obd-gq-section" style={{ padding: 0 }}>
            <GenerateQuoteSectionHead
                title={sectionTitle}
                stats={solarHeaderStats}
                ariaLabel={`${sectionTitle} quote totals`}
            />
            <div className="obd-gq-section-body">
            <div
                className={`obd-generate-quote-split${lineMode === 'battery' || lineMode === 'solar' ? ' obd-generate-quote-split--no-aside' : ''}`}
            >
                <div className="obd-generate-quote-split__forms">
                    <div className="obd-gq-product-line">
                        {confirmQuoteCellKey?.partner} — {confirmQuoteCellKey?.product}
                    </div>
                <div className="obd-utility__fields obd-generate-quote-form-fields">
                    <div className="obd-utility__field">
                        <label className="obd-utility__field-label">Finance Method</label>
                        <OBDSelect
                            className="obd-utility__select"
                            value={financeMethod}
                            disabled
                            options={[{ value: financeMethod, label: financeMethod }]}
                        />
                    </div>
                    {/* Amount & Price-as-Slider: third arg `true` on change defers totals/parent sync until blur. Slider+Currency share one Price via setSolarQuoteFromValues. */}
                    {solarQuoteFormFields
                        .filter((field) => field.enable)                        
                        .map((field: any) => {
                            const isPriceSlider = field.type === 'Slider' && field.name === 'Price';
                            let nzMin: number | undefined;
                            let nzMax: number | undefined;
                            let price = 0;
                            let handleSliderChange: (value: number | number[]) => void = () => {};
                            let handleSliderCurrencyChange: (value: string | undefined) => void = () => {};

                            if (isPriceSlider) {
                                const nMin = Number(field.range?.nzMin);
                                const nMax = Number(field.range?.nzMax);
                                nzMin = Number.isFinite(nMin) ? nMin : undefined;
                                nzMax = Number.isFinite(nMax) ? nMax : undefined;
                                // Single source of truth for the slider handle: `field.value`, same as the adjustment
                                // CurrencyInput. Preferring `range.nzValue` let the track drift from the stored price
                                // when `nzValue` was out of sync; bounds still come from `range`.
                                const rawPrice = parseAmountDisplayString(String(field.value ?? '0'));
                                price = clampPriceToSliderRange(
                                    Number.isFinite(rawPrice) ? rawPrice : 0,
                                    nzMin,
                                    nzMax,
                                );

                                handleSliderChange = (value: number | number[]) => {
                                    const v = Array.isArray(value) ? value[0] : value;
                                    const clamped = clampPriceToSliderRange(Number(v), nzMin, nzMax);
                                    setSolarQuoteFromValues(field.name, clamped);
                                };

                                handleSliderCurrencyChange = (value: string | undefined) => {
                                    if (value === undefined) return;
                                    const v = value ?? '';
                                    amountDraftRef.current[field.name] = v;
                                    if (v.trim() === '') {
                                        setSolarQuoteFromValues(field.name, v, true);
                                        return;
                                    }
                                    const parsed = parseAmountDisplayString(v);
                                    if (!Number.isFinite(parsed)) {
                                        setSolarQuoteFromValues(field.name, v, true);
                                        return;
                                    }
                                    const clamped = clampPriceToSliderRange(parsed, nzMin, nzMax);
                                    setSolarQuoteFromValues(field.name, String(clamped), true);
                                };
                            }

                            return (
                        <div className="obd-utility__field" key={field.name}>
                            <label className="obd-utility__field-label">{field.label}</label>
                            {field.type === 'amount' && (

                                <CurrencyInput
                                    className="obd-utility__input"
                                    name={field.name}
                                    value={
                                        amountDraftRef.current[field.name] ??
                                        (field.value === undefined || field.value === null || String(field.value).trim() === ''
                                            ? ''
                                            : String(field.value))
                                    }
                                    onKeyDown={(e) => validateFieldValue(e, field)}
                                    onValueChange={(value) => {
                                        const v = value ?? '';
                                        amountDraftRef.current[field.name] = v;
                                        setSolarQuoteFromValues(field.name, v, true);
                                    }}
                                    onBlur={() => {
                                        setSolarQuoteFromValues(
                                            field.name,
                                            amountDraftRef.current[field.name] ?? String(field.value ?? ''),
                                            false,
                                        );
                                        delete amountDraftRef.current[field.name];
                                    }}
                                    prefix="$"
                                    decimalsLimit={
                                        field.name === 'Price'
                                            ? priceDecimalsLimit
                                            : DEFAULT_AMOUNT_DECIMALS_LIMIT
                                    }
                                    allowNegativeValue={false}
                                />
                            )}
                            {field.type === 'dropdown' && (
                                <OBDSelect
                                    className="obd-utility__select"
                                    disabled={!field.enable}
                                    placeholder={field?.name !== 'PricingMethod' ? 'Select' : undefined}
                                    value={field.value === '' ? undefined : field.value}
                                    onChange={(value) =>
                                        setSolarQuoteFromValues(
                                            field.name,
                                            value,
                                            false,
                                            field.name === 'PricingMethod' || field.name === 'Escalator',
                                        )
                                    }
                                    options={(field?.options ?? []).map((option: any) => ({
                                        value: option.Value,
                                        label: option.Text,
                                    }))}
                                />
                            )}
                            {isPriceSlider && (
                                <div className="obd-price-slider-row">
                                    <div className="obd-price-slider-row__track">
                                        <Slider
                                            className="obd-price-slider"
                                            style={getSliderCssVars(field.range?.style)}
                                            min={nzMin}
                                            max={nzMax}
                                            step={field.range?.nzStep}
                                            marks={field.range?.nzMarks}
                                            value={price}
                                            onChange={handleSliderChange}
                                            tooltip={{ formatter: () => `${price}` }}
                                            disabled={!nzMin || !nzMax}
                                        />
                                        { getSliderRateNote(field) && (
                                            <p className="slider-rate-note">
                                                <span style={{ color: getSliderRateNoteColor(field)}}>
                                                    <b>{ getFieldDetails(field.name)?.value } </b> 
                                                </span>
                                                <span dangerouslySetInnerHTML={{ __html:getSliderRateNote(field)}}></span>
                                            </p>
                                        )}   
                                        
                                    </div>                           
                                    <div className="obd-price-adjustment-container">
                                        <label
                                            className="obd-utility__field-label"
                                            htmlFor="obd-solar-price-adjustment-input"
                                        >
                                            {priceAdjustmentSectionLabel}
                                        </label>
                                        <CurrencyInput
                                            id="obd-solar-price-adjustment-input"
                                            className="obd-utility__input obd-price-slider-row__input"
                                            name={field.name}
                                            value={
                                                amountDraftRef.current[field.name] ??
                                                (field.value === undefined || field.value === null || String(field.value).trim() === ''
                                                    ? ''
                                                    : String(field.value))
                                            }
                                            onKeyDown={(e) => validateFieldValue(e, field)}
                                            onValueChange={handleSliderCurrencyChange}
                                            onBlur={() => {
                                                const raw =
                                                    amountDraftRef.current[field.name] ?? String(field.value ?? '');
                                                const n = parseAmountDisplayString(raw);
                                                // const clamped = Number.isFinite(n) && raw.trim() !== ''
                                                //     ? clampPriceToSliderRange(n, nzMin, nzMax)
                                                //     : price;
                                                setSolarQuoteFromValues(field.name, String(n), false);
                                                delete amountDraftRef.current[field.name];
                                            }}
                                            prefix="$"
                                            decimalsLimit={3}
                                            allowNegativeValue={true}
                                        />

                                    </div>
                                </div>
                            )}
                            {field.message && <span className='validation-message'>{field.message}</span>}
                        </div>
                            );
                        })}
                </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default SolarQuoteDetails;
